import React from 'react';
import { X, Crown, Sparkles, ArrowRight, Zap, Shield, Infinity } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useLocaleContext } from '../context/LocaleContext';
import type { Locale } from '../types/locale';

interface PremiumUpgradeModalProps {
  isOpen: boolean;
  onClose: () => void;
  feature?: string; // Optional: specific feature that triggered the modal
}

const translations: Record<Locale, {
  title: string;
  message: string;
  features: { icon: typeof Infinity; text: string }[];
  upgradeButton: string;
  cancelButton: string;
  pricingHighlight: string;
}> = {
  en: {
    title: 'Premium Required',
    message: 'Upgrade to Premium to unlock all features and get unlimited access.',
    features: [
      { icon: Infinity, text: 'Unlimited character processing' },
      { icon: Zap, text: 'Unlimited scans with no cooldown' },
      { icon: Shield, text: 'Clean documents (.docx & .pages)' },
      { icon: Sparkles, text: 'No secret watermarks added' }
    ],
    upgradeButton: 'Upgrade Now',
    cancelButton: 'Maybe Later',
    pricingHighlight: 'Lifetime access for just $27.36 – Save 75%!'
  },
  de: {
    title: 'Premium erforderlich',
    message: 'Upgrade auf Premium, um alle Funktionen freizuschalten und unbegrenzten Zugriff zu erhalten.',
    features: [
      { icon: Infinity, text: 'Unbegrenzte Zeichenanzahl' },
      { icon: Zap, text: 'Unbegrenzte Scans ohne Wartezeit' },
      { icon: Shield, text: 'Dokumente bereinigen (.docx & .pages)' },
      { icon: Sparkles, text: 'Keine versteckten Wasserzeichen' }
    ],
    upgradeButton: 'Jetzt upgraden',
    cancelButton: 'Später',
    pricingHighlight: 'Lebenslanger Zugang für nur $27.36 – 75% Ersparnis!'
  },
  'pt-BR': {
    title: 'Premium Necessário',
    message: 'Faça o upgrade para o Premium e desbloqueie todos os recursos com acesso ilimitado.',
    features: [
      { icon: Infinity, text: 'Processamento ilimitado de caracteres' },
      { icon: Zap, text: 'Verificações ilimitadas sem tempo de espera' },
      { icon: Shield, text: 'Limpar documentos (.docx e .pages)' },
      { icon: Sparkles, text: 'Sem marcas d\'água ocultas' }
    ],
    upgradeButton: 'Atualizar Agora',
    cancelButton: 'Talvez Depois',
    pricingHighlight: 'Acesso vitalício por apenas $27.36 – Economize 75%!'
  },
  'ms-MY': {
    title: 'Premium Diperlukan',
    message: 'Naik taraf ke Premium untuk membuka semua ciri dan dapatkan akses tanpa had.',
    features: [
      { icon: Infinity, text: 'Pemprosesan aksara tanpa had' },
      { icon: Zap, text: 'Imbasan tanpa had tanpa masa menunggu' },
      { icon: Shield, text: 'Bersihkan dokumen (.docx & .pages)' },
      { icon: Sparkles, text: 'Tiada tera air tersembunyi ditambah' }
    ],
    upgradeButton: 'Naik Taraf Sekarang',
    cancelButton: 'Mungkin Nanti',
    pricingHighlight: 'Akses seumur hidup hanya $27.36 – Jimat 75%!'
  },
  es: {
    title: 'Se Requiere Premium',
    message: 'Actualiza a Premium para desbloquear todas las funciones y obtener acceso ilimitado.',
    features: [
      { icon: Infinity, text: 'Procesamiento de caracteres ilimitado' },
      { icon: Zap, text: 'Escaneos ilimitados sin tiempo de espera' },
      { icon: Shield, text: 'Limpiar documentos (.docx y .pages)' },
      { icon: Sparkles, text: 'Sin marcas de agua ocultas' }
    ],
    upgradeButton: 'Actualizar Ahora',
    cancelButton: 'Quizás Luego',
    pricingHighlight: 'Acceso de por vida por solo $27.36 – ¡Ahorra 75%!'
  },
  fr: {
    title: 'Premium Requis',
    message: 'Passez à Premium pour débloquer toutes les fonctionnalités et bénéficier d\'un accès illimité.',
    features: [
      { icon: Infinity, text: 'Traitement de caractères illimité' },
      { icon: Zap, text: 'Analyses illimitées sans délai d\'attente' },
      { icon: Shield, text: 'Nettoyer les documents (.docx et .pages)' },
      { icon: Sparkles, text: 'Aucun filigrane caché ajouté' }
    ],
    upgradeButton: 'Améliorer Maintenant',
    cancelButton: 'Peut-être Plus Tard',
    pricingHighlight: 'Accès à vie pour seulement $27.36 – Économisez 75% !'
  },
  ja: {
    title: 'プレミアムが必要です',
    message: 'プレミアムにアップグレードして、すべての機能をアンロックし、無制限のアクセスを取得しましょう。',
    features: [
      { icon: Infinity, text: '無制限の文字処理' },
      { icon: Zap, text: 'クールダウンなしの無制限スキャン' },
      { icon: Shield, text: 'ドキュメントのクリーニング（.docx & .pages）' },
      { icon: Sparkles, text: '隠し透かしなし' }
    ],
    upgradeButton: '今すぐアップグレード',
    cancelButton: 'また後で',
    pricingHighlight: '生涯アクセスがわずか$27.36 – 75%オフ！'
  },
  id: {
    title: 'Premium Diperlukan',
    message: 'Tingkatkan ke Premium untuk membuka semua fitur dan mendapatkan akses tanpa batas.',
    features: [
      { icon: Infinity, text: 'Pemrosesan karakter tanpa batas' },
      { icon: Zap, text: 'Pemindaian tanpa batas tanpa waktu tunggu' },
      { icon: Shield, text: 'Bersihkan dokumen (.docx & .pages)' },
      { icon: Sparkles, text: 'Tidak ada watermark tersembunyi' }
    ],
    upgradeButton: 'Tingkatkan Sekarang',
    cancelButton: 'Mungkin Nanti',
    pricingHighlight: 'Akses seumur hidup hanya $27.36 – Hemat 75%!'
  }
};

const PremiumUpgradeModal: React.FC<PremiumUpgradeModalProps> = ({
  isOpen,
  onClose,
  feature
}) => {
  const navigate = useNavigate();
  const { locale } = useLocaleContext();

  if (!isOpen) return null;

  const handleUpgrade = () => {
    navigate('/pricing');
    onClose();
  };

  const t = translations[locale] || translations.en;

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-charcoal-900 rounded-3xl shadow-2xl max-w-lg w-full mx-4 border border-neutral-200 dark:border-charcoal-700 transform animate-in fade-in zoom-in duration-300 relative overflow-hidden">
        {/* Background decorations */}
        <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-accent-400/20 to-purple-400/20 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 left-0 w-24 h-24 bg-gradient-to-tr from-purple-400/20 to-accent-400/20 rounded-full blur-2xl"></div>

        <div className="relative p-8">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-gradient-to-r from-accent-500 to-purple-600 rounded-xl text-white">
                <Crown className="w-6 h-6" />
              </div>
              <h4 className="text-xl font-bold text-neutral-900 dark:text-white">
                {t.title}
              </h4>
            </div>
            <button
              onClick={onClose}
              className="text-neutral-500 hover:text-neutral-700 dark:text-neutral-400 dark:hover:text-neutral-200 transition-colors p-2 hover:bg-neutral-100 dark:hover:bg-charcoal-800 rounded-lg"
              aria-label="Close modal"
            >
              <X size={20} />
            </button>
          </div>

          {/* Content */}
          <div className="mb-6">
            <p className="text-neutral-600 dark:text-neutral-300 mb-6 leading-relaxed">
              {t.message}
            </p>

            {/* Premium Features List */}
            <div className="space-y-3 mb-6">
              {t.features.map((feat, index) => (
                <div
                  key={index}
                  className="flex items-center gap-3 p-3 bg-gradient-to-r from-accent-50 to-purple-50 dark:from-accent-900/20 dark:to-purple-900/20 rounded-xl border border-accent-200 dark:border-accent-800"
                >
                  <div className="flex-shrink-0 w-8 h-8 rounded-lg bg-gradient-to-r from-accent-500 to-purple-600 flex items-center justify-center text-white">
                    <feat.icon size={16} />
                  </div>
                  <span className="text-sm font-medium text-neutral-700 dark:text-neutral-300">
                    {feat.text}
                  </span>
                </div>
              ))}
            </div>

            {/* Pricing highlight */}
            <div className="bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 rounded-xl p-4 border border-green-200 dark:border-green-800">
              <p className="text-sm text-center text-green-700 dark:text-green-300 font-semibold">
                {t.pricingHighlight}
              </p>
            </div>
          </div>

          {/* Actions */}
          <div className="flex flex-col sm:flex-row gap-3">
            <button
              onClick={handleUpgrade}
              className="group flex items-center justify-center gap-3 px-6 py-4 bg-gradient-to-r from-accent-600 to-purple-600 hover:from-accent-700 hover:to-purple-700 text-white rounded-2xl font-bold transition-all duration-300 transform hover:scale-[1.02] active:scale-[0.98] shadow-lg hover:shadow-xl flex-1"
            >
              <Crown className="w-5 h-5" />
              <span>{t.upgradeButton}</span>
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform duration-300" />
            </button>

            <button
              onClick={onClose}
              className="px-6 py-4 bg-neutral-200 dark:bg-charcoal-800 text-neutral-800 dark:text-neutral-200 rounded-2xl hover:bg-neutral-300 dark:hover:bg-charcoal-700 transition-all duration-300 transform hover:scale-[1.02] active:scale-[0.98] font-medium"
            >
              {t.cancelButton}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PremiumUpgradeModal;
